import React, { useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi';
import { formatPeriode } from '../EstateDashboardComponent';
import { CustomTooltipLhp } from '../CustomTooltip';

const COLORS = {
    Restan: '#CA6060',
    Internal: '#82CA9D',
    Eksternal: '#8884D8',
    Olah: '#FFC658',
};

export interface LHPTrendDatum {
    periode: string;
    date: string;
    Restan: number;
    Internal: number;
    Eksternal: number;
    Olah: number;
    'Jam Olah': number;
}

interface LHPProduksiTrendProps {
    data: LHPTrendDatum[];
}

const LHPProduksiTrend: React.FC<LHPProduksiTrendProps> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let internalOpacity = 1;
    let eksternalOpacity = 1;
    let olahOpacity = 1;

    if (hoveringDataKey === 'Internal') {
        eksternalOpacity = 0.5;
        olahOpacity = 0.5;
    } else if (hoveringDataKey === 'Eksternal') {
        internalOpacity = 0.5;
        olahOpacity = 0.5;
    } else if (hoveringDataKey === 'Olah') {
        internalOpacity = 0.5;
        eksternalOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    // Aggregate daily data by periode for monthly totals
    const aggregatedByPeriode = React.useMemo(() => {
        const grouped: Record<string, { Restan: number; Internal: number; Eksternal: number; Olah: number }> = {};
        
        data.forEach(d => {
            if (!grouped[d.periode]) {
                grouped[d.periode] = { Restan: 0, Internal: 0, Eksternal: 0, Olah: 0 };
            }
            grouped[d.periode].Restan += d.Restan;
            grouped[d.periode].Internal += d.Internal;
            grouped[d.periode].Eksternal += d.Eksternal;
            grouped[d.periode].Olah += d.Olah;
        });
        
        return Object.entries(grouped).map(([periode, values]) => {
            const Internal = values.Internal / 1000;
            const Eksternal = values.Eksternal / 1000;
            return {
                periode,
                Restan: values.Restan / 1000,
                Internal,
                Eksternal,
                Olah: values.Olah / 1000,
                IntPlusExt: Internal + Eksternal,
            };
        });
    }, [data]);

    const scaledData = aggregatedByPeriode;
    const year = data[0]?.periode?.slice(0, 4) || new Date().getFullYear().toString();
    return (
        <Box p={4} mb={4} height="100%" w="100%">
            <Text fontSize="xl" fontWeight="semibold" mb={4}>
                Produksi TBS Bulanan {year}
            </Text>
            <Box 
                // flex={1} 
                minH="130px" 
                height="350px"
                // minWidth="690px"
                // overflowX="auto" 
                // overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={380}>
                    <ComposedChart data={scaledData} barGap={0}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="periode"
                            tickFormatter= {formatPeriode}
                            interval={0}
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            tickFormatter={(v) => formatNumber(v, 'number')}
                            width={80}
                            tick={{ fontSize: 12 }}
                            domain={[0, 'auto']}
                            label={{
                                value: 'Ton',
                                angle: -90,
                                position: 'insideLeft',
                                offset: 5,
                                fontSize: 14,
                                fill: '#666',
                            }}
                        />
                        <Tooltip content={<CustomTooltipLhp />} wrapperStyle={{ zIndex: 1000 }} />
                        <Legend height={60} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
                        <Bar
                            name="TBS Internal"
                            dataKey="Internal"
                            stackId="a"
                            fill={COLORS.Internal}
                            fillOpacity={internalOpacity}
                        />
                        <Bar
                            name="TBS Eksternal"
                            dataKey="Eksternal"
                            stackId="a"
                            fill={COLORS.Eksternal}
                            fillOpacity={eksternalOpacity}
                        />
                        <Bar
                            name="Olah"
                            dataKey="Olah"
                            fill={COLORS.Olah}
                            fillOpacity={olahOpacity}
                        />
                        {/* <Line
                            name="Olah"
                            type="monotone"
                            dataKey="Olah"
                            stroke={COLORS.Olah}
                            strokeWidth={2}
                            dot={{ r: 7 }}
                            activeDot={{ r: 7 }}
                        /> */}
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default LHPProduksiTrend;